import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Wand2, FileSignature, Download, Check,
  FileText, ExternalLink, LinkIcon, Unlink, MessageSquarePlus,
} from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/lib/utils';
import { DraftOnlyLabel } from '@/components/pv/DraftOnlyLabel';

interface SourceDocument {
  id: string;
  fileName: string;
  storageKey: string;
  mimeType: string;
  fileSizeBytes: number;
}

interface DocOption {
  id: string;
  fileName: string;
}

export function PVCaseDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();

  const kase = useQuery({
    queryKey: ['pv-case', id],
    queryFn: async () => (await api.get(`/api/pv/cases/${id}`)).data,
  });

  const [codeResult, setCodeResult] = useState<MeddraResult | null>(null);
  const [esignOpen, setEsignOpen] = useState(false);
  const [esignPw, setEsignPw] = useState('');
  const [esignReason, setEsignReason] = useState('');
  const [sourceViewOpen, setSourceViewOpen] = useState(false);
  const [sourceBlobUrl, setSourceBlobUrl] = useState<string | null>(null);
  const [linkDocOpen, setLinkDocOpen] = useState(false);

  // Follow-up state
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [fuRequestedFrom, setFuRequestedFrom] = useState('');
  const [fuMethod, setFuMethod] = useState<'email' | 'phone' | 'letter' | 'fax'>('email');
  const [fuNotes, setFuNotes] = useState('');
  const [fuDueDate, setFuDueDate] = useState('');

  const followUps = useQuery({
    queryKey: ['pv-case-follow-ups', id],
    queryFn: async () => (await api.get(`/api/pv/cases/${id}/follow-ups`)).data as FollowUpItem[],
  });

  const createFollowUp = useMutation({
    mutationFn: async () =>
      api.post(`/api/pv/cases/${id}/follow-ups`, {
        requestedFrom: fuRequestedFrom,
        requestMethod: fuMethod,
        requestNotes: fuNotes || undefined,
        dueDate: fuDueDate ? new Date(fuDueDate).toISOString() : undefined,
      }),
    onSuccess: () => {
      toast.success('Follow-up requested', 'The follow-up has been created');
      setFollowUpOpen(false);
      setFuRequestedFrom('');
      setFuMethod('email');
      setFuNotes('');
      setFuDueDate('');
      qc.invalidateQueries({ queryKey: ['pv-case-follow-ups', id] });
      qc.invalidateQueries({ queryKey: ['pv-case', id] });
    },
    onError: (err) => toast.error('Follow-up failed', extractApiError(err).message),
  });

  const tenantDocs = useQuery({
    queryKey: ['tenant-docs-for-link'],
    queryFn: async () => (await api.get('/api/documents?pageSize=200')).data,
    enabled: linkDocOpen,
  });

  const codeMutation = useMutation({
    mutationFn: async () => (await api.post(`/api/pv/cases/${id}/code`, {})).data,
    onSuccess: (data) => {
      setCodeResult(data as MeddraResult);
      toast.success('MedDRA suggestion', `${data.suggestion.suggestedPT?.term ?? 'see alternatives'} — model: ${data.model}`);
    },
    onError: (err) => toast.error('Coding failed', extractApiError(err).message),
  });

  const narrativeMutation = useMutation({
    mutationFn: async () => (await api.post(`/api/pv/cases/${id}/narrative`, { style: 'standard' })).data,
    onSuccess: () => {
      toast.success('Narrative drafted', 'See draft narrative below');
      qc.invalidateQueries({ queryKey: ['pv-case', id] });
    },
    onError: (err) => toast.error('Narrative failed', extractApiError(err).message),
  });

  const approveMutation = useMutation({
    mutationFn: async () =>
      api.post(`/api/pv/cases/${id}/approve-narrative`, {
        esignature: { password: esignPw, reason: esignReason },
      }),
    onSuccess: () => {
      toast.success('Narrative approved', 'E-signature recorded');
      setEsignOpen(false);
      setEsignPw('');
      setEsignReason('');
      qc.invalidateQueries({ queryKey: ['pv-case', id] });
    },
    onError: (err) => toast.error('Approval failed', extractApiError(err).message),
  });

  const transitionMutation = useMutation({
    mutationFn: async (toStatus: string) => api.post(`/api/pv/cases/${id}/transition`, { toStatus }),
    onSuccess: (_, toStatus) => {
      toast.success('Case transitioned', `Moved to ${toStatus}`);
      qc.invalidateQueries({ queryKey: ['pv-case', id] });
    },
    onError: (err) => toast.error('Transition failed', extractApiError(err).message),
  });

  const linkDocMutation = useMutation({
    mutationFn: async (docId: string | null) =>
      api.patch(`/api/pv/cases/${id}`, { sourceDocumentId: docId }),
    onSuccess: () => {
      toast.success('Source updated', 'Source document linked to case');
      setLinkDocOpen(false);
      qc.invalidateQueries({ queryKey: ['pv-case', id] });
    },
    onError: (err) => toast.error('Link failed', extractApiError(err).message),
  });

  async function downloadE2B() {
    try {
      const res = await api.get(`/api/pv/cases/${id}/e2b`, { responseType: 'blob' });
      const blob = new Blob([res.data as BlobPart], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `e2b-${kase.data.caseNumber}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed', extractApiError(err).message);
    }
  }

  if (kase.isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (!kase.data) return <div>Not found</div>;

  const c = kase.data;
  const srcDoc: SourceDocument | null = c.sourceDocument ?? null;

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/pv"><ArrowLeft className="mr-2 h-4 w-4" /> PV cases</Link>
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{c.caseNumber}</h1>
            <p className="text-sm text-muted-foreground">{c.suspectProduct} · {c.sourceType}/{c.sourceChannel}</p>
          </div>
          <Badge variant={c.status === 'SUBMITTED' || c.status === 'CLOSED' ? 'success' : 'warning'}>
            {c.status.replace('_', ' ')}
          </Badge>
        </div>
      </div>

      {/* Source document traceability */}
      <Card className={srcDoc ? 'border-blue-200 bg-blue-50/30' : undefined}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Source document
            </CardTitle>
            <div className="flex gap-2">
              {srcDoc && (
                <Button size="sm" variant="outline" onClick={async () => {
                  if (!sourceBlobUrl) {
                    try {
                      const res = await api.get(`/api/documents/${srcDoc.id}/file`, { responseType: 'blob' });
                      const url = URL.createObjectURL(res.data as Blob);
                      setSourceBlobUrl(url);
                      setSourceViewOpen(true);
                    } catch { toast.error('Failed to load source', 'Could not fetch document'); }
                  } else {
                    setSourceViewOpen(true);
                  }
                }}>
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> View source
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setLinkDocOpen(true)}>
                {srcDoc ? <><Unlink className="mr-1.5 h-3.5 w-3.5" /> Change</> : <><LinkIcon className="mr-1.5 h-3.5 w-3.5" /> Link document</>}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {srcDoc ? (
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-medium">{srcDoc.fileName}</div>
                <div className="text-xs text-muted-foreground">
                  {srcDoc.mimeType} · {(srcDoc.fileSizeBytes / 1024).toFixed(0)} KB
                </div>
              </div>
              <Badge variant="info" className="ml-auto">Source linked</Badge>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4 text-center text-sm text-amber-800">
              No source document linked. For GVP compliance, link the original report (literature article, email, spontaneous report) to enable source traceability.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Patient + reporter</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Initials" value={c.patientInitials ?? '—'} />
            <Row label="Age / sex" value={`${c.patientAgeYears ?? '—'} / ${c.patientSex ?? '—'}`} />
            <Row label="Reporter qualification" value={c.reporterQualification ?? '—'} />
            <Row label="Country" value={c.reporterCountry ?? '—'} />
            <Row label="Receipt date" value={formatDate(c.receiptDate)} />
            <Row label="Reporting due" value={c.reportingDeadline ? formatDate(c.reportingDeadline) : '—'} />
            <Row label="Expedited?" value={c.isExpedited ? 'Yes (15-day)' : '90-day'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Event + coding</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Verbatim</div>
              <div className="rounded-md bg-muted p-2 text-xs">{c.adverseEventText}</div>
            </div>
            <Row label="MedDRA PT" value={c.meddraPT ?? '—'} />
            <Row label="MedDRA SOC" value={c.meddraSOC ?? '—'} />
            <Row label="Seriousness" value={c.seriousness ?? '—'} />
            <Row label="Causality" value={c.causality ?? '—'} />
            <Row label="Outcome" value={c.outcome ?? '—'} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>AI actions</CardTitle>
          <CardDescription>
            All AI outputs require human review and approval before regulatory submission.
            {srcDoc && ' Source document is automatically provided as context.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button size="sm" onClick={() => codeMutation.mutate()} disabled={codeMutation.isPending}>
              <Wand2 className="mr-1.5 h-3.5 w-3.5" />
              {codeMutation.isPending ? 'Coding...' : 'Suggest MedDRA code'}
            </Button>
            <Button size="sm" onClick={() => narrativeMutation.mutate()} disabled={narrativeMutation.isPending}>
              <FileSignature className="mr-1.5 h-3.5 w-3.5" />
              {narrativeMutation.isPending ? 'Drafting...' : 'Draft narrative'}
            </Button>
            <Button size="sm" variant="outline" onClick={downloadE2B}>
              <Download className="mr-1.5 h-3.5 w-3.5" /> Export E2B(R3)
            </Button>
          </div>

          {codeResult && (
            <div className="rounded-md border bg-muted/30 p-3 text-xs">
              <div className="font-medium">MedDRA suggestion</div>
              <div className="mt-1 space-y-1">
                <div>PT: <span className="font-mono">{codeResult.suggestion.suggestedPT?.term ?? '—'}</span> {codeResult.suggestion.suggestedPT?.code && <span className="text-muted-foreground">({codeResult.suggestion.suggestedPT.code})</span>}</div>
                <div>LLT: <span className="font-mono">{codeResult.suggestion.suggestedLLT?.term ?? '—'}</span> {codeResult.suggestion.suggestedLLT?.code && <span className="text-muted-foreground">({codeResult.suggestion.suggestedLLT.code})</span>}</div>
                <div>SOC: <span className="font-mono">{codeResult.suggestion.suggestedSOC?.term ?? '—'}</span> {codeResult.suggestion.suggestedSOC?.code && <span className="text-muted-foreground">({codeResult.suggestion.suggestedSOC.code})</span>}</div>
              </div>
              {srcDoc && (
                <div className="mt-2 flex items-center gap-1.5 text-blue-700">
                  <FileText className="h-3 w-3" />
                  <span>Coded against: {srcDoc.fileName}</span>
                  <button className="underline" onClick={() => { if (sourceBlobUrl) setSourceViewOpen(true); }}>view source</button>
                </div>
              )}
              <DraftOnlyLabel context="meddra" className="mt-2" />
            </div>
          )}

          {c.draftNarrative && (
            <div className="rounded-md border p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Draft narrative</div>
                {c.narrativeApprovedBy ? (
                  <Badge variant="success"><Check className="mr-1 h-3 w-3" /> Approved</Badge>
                ) : (
                  <Button size="sm" onClick={() => setEsignOpen(true)}>Sign + approve</Button>
                )}
              </div>
              {!c.narrativeApprovedBy && <DraftOnlyLabel context="narrative" className="mt-2" />}
              {srcDoc && !c.narrativeApprovedBy && (
                <div className="mt-2 flex items-center gap-1.5 rounded bg-blue-50 px-2 py-1 text-xs text-blue-700">
                  <FileText className="h-3 w-3 shrink-0" />
                  Review this narrative against the source document before approving.
                  <button className="ml-1 font-medium underline" onClick={() => { if (sourceBlobUrl) setSourceViewOpen(true); }}>Open source</button>
                </div>
              )}
              <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap text-xs">{c.draftNarrative}</pre>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Follow-up requests */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Follow-up requests</CardTitle>
            <Button size="sm" onClick={() => setFollowUpOpen(true)}>
              <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" /> Request follow-up
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {followUps.isLoading && <div className="text-sm text-muted-foreground">Loading...</div>}
          {followUps.data && followUps.data.length === 0 && (
            <div className="text-sm text-muted-foreground">No follow-up requests yet.</div>
          )}
          {followUps.data && followUps.data.length > 0 && (
            <div className="space-y-2">
              {followUps.data.map((fu) => (
                <div key={fu.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                  <div className="space-y-0.5">
                    <div className="font-medium">{fu.requestedFrom}</div>
                    <div className="text-xs text-muted-foreground">
                      via {fu.requestMethod} · {formatDate(fu.requestDate)}
                      {fu.dueDate && <> · due {formatDate(fu.dueDate)}</>}
                    </div>
                    {fu.responseNotes && (
                      <div className="mt-1 text-xs text-muted-foreground italic">{fu.responseNotes}</div>
                    )}
                  </div>
                  <Badge
                    variant={
                      fu.status === 'received' ? 'success' :
                      fu.status === 'pending' ? 'warning' :
                      fu.status === 'overdue' ? 'destructive' :
                      'secondary'
                    }
                  >
                    {fu.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Follow-up request dialog */}
      <Dialog open={followUpOpen} onOpenChange={setFollowUpOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request follow-up</DialogTitle>
            <DialogDescription>
              Request additional information from the reporter or another party.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Requested from</Label>
              <Input
                value={fuRequestedFrom}
                onChange={(e) => setFuRequestedFrom(e.target.value)}
                placeholder="Reporter name or email"
              />
            </div>
            <div>
              <Label>Method</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={fuMethod}
                onChange={(e) => setFuMethod(e.target.value as 'email' | 'phone' | 'letter' | 'fax')}
              >
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="letter">Letter</option>
                <option value="fax">Fax</option>
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={fuNotes}
                onChange={(e) => setFuNotes(e.target.value)}
                placeholder="What information do you need?"
                rows={3}
              />
            </div>
            <div>
              <Label>Due date</Label>
              <Input
                type="date"
                value={fuDueDate}
                onChange={(e) => setFuDueDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowUpOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createFollowUp.mutate()}
              disabled={createFollowUp.isPending || !fuRequestedFrom.trim()}
            >
              {createFollowUp.isPending ? 'Requesting...' : 'Send request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader><CardTitle>Status transition</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {['TRIAGE','DATA_ENTRY','CODING','MEDICAL_REVIEW','NARRATIVE','QC','SUBMISSION_READY','SUBMITTED','CLOSED','REJECTED'].map((s) => (
              <Button
                key={s}
                size="sm"
                variant={s === c.status ? 'default' : 'outline'}
                disabled={s === c.status || transitionMutation.isPending}
                onClick={() => transitionMutation.mutate(s)}
              >
                {s.replace('_', ' ')}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Actions log</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-xs">
          {c.actions?.map((a: { id: string; action: string; notes: string | null; createdAt: string }) => (
            <div key={a.id} className="flex justify-between border-b py-1.5 last:border-0">
              <span>{a.action}{a.notes ? ` — ${a.notes}` : ''}</span>
              <span className="text-muted-foreground">{formatDate(a.createdAt)}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* E-signature dialog */}
      <Dialog open={esignOpen} onOpenChange={setEsignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve narrative — e-signature required</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Password</Label>
              <Input type="password" autoFocus value={esignPw} onChange={(e) => setEsignPw(e.target.value)} />
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={esignReason} onChange={(e) => setEsignReason(e.target.value)} placeholder="e.g. Reviewed against source document, content accurate" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEsignOpen(false)}>Cancel</Button>
            <Button onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}>
              {approveMutation.isPending ? 'Signing...' : 'Sign & approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Source document viewer dialog */}
      {srcDoc && (
        <Dialog open={sourceViewOpen} onOpenChange={setSourceViewOpen}>
          <DialogContent className="max-w-4xl h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {srcDoc.fileName}
              </DialogTitle>
              <DialogDescription>
                Source document for case {c.caseNumber}. Verify AI outputs against this original.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-hidden rounded-lg border">
              {sourceBlobUrl ? (
                <iframe
                  src={sourceBlobUrl}
                  className="h-full w-full"
                  title={srcDoc.fileName}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Loading document...
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Link document dialog */}
      <Dialog open={linkDocOpen} onOpenChange={setLinkDocOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link source document</DialogTitle>
            <DialogDescription>
              Select the original document (literature article, spontaneous report, email) that this ICSR was created from.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 space-y-1 overflow-auto">
            {tenantDocs.isLoading && <div className="text-sm text-muted-foreground">Loading documents...</div>}
            {tenantDocs.data?.items?.map((d: DocOption) => (
              <button
                key={d.id}
                className="flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm hover:bg-muted/50"
                onClick={() => linkDocMutation.mutate(d.id)}
                disabled={linkDocMutation.isPending}
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{d.fileName}</span>
                {d.id === srcDoc?.id && <Badge variant="info" className="ml-auto shrink-0">Current</Badge>}
              </button>
            ))}
          </div>
          <DialogFooter>
            {srcDoc && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => linkDocMutation.mutate(null)}
                disabled={linkDocMutation.isPending}
              >
                <Unlink className="mr-1.5 h-3.5 w-3.5" /> Unlink
              </Button>
            )}
            <Button variant="outline" onClick={() => setLinkDocOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface MeddraResult {
  suggestion: {
    suggestedPT: { term: string; code: string | null } | null;
    suggestedLLT: { term: string; code: string | null } | null;
    suggestedSOC: { term: string; code: string | null } | null;
  };
  model: string;
  cost: number;
}

interface FollowUpItem {
  id: string;
  requestDate: string;
  requestedFrom: string;
  requestMethod: string;
  requestNotes: string | null;
  dueDate: string | null;
  responseDate: string | null;
  responseNotes: string | null;
  status: string;
  createdAt: string;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b pb-1.5 last:border-0 last:pb-0">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
